import type { Brand, GetBrandsQuery } from "@src/__generated__/graphql";
import type { Maybe } from "@src/__generated__/urql-graphql";
import { computed, makeObservable, observable } from "mobx";
import type { DeepPick } from "ts-essentials";

type TBrand = NonNullable<GetBrandsQuery["brands"]>["data"][0];
type TClient = NonNullable<TBrand["client"]>;
type TContact = NonNullable<TBrand["contacts"]>[0];
type TCreditAccount = NonNullable<TBrand["creditAccounts"]>[0];
type TBrandStats = NonNullable<TBrand["stats"]>;
type TPipedriveCustomFieldValue = NonNullable<
  TBrand["pipedriveCustomFieldValues"]
>[0];

export class BrandModel {
  id: Brand["id"];
  name: Brand["name"];
  code: Brand["code"];
  image: DeepPick<
    Brand["image"],
    {
      urls: {
        thumbnail: never;
      };
    }
  >;
  archived_at: Brand["archived_at"];
  client: TClient;
  contacts: TContact[];
  creditAccounts: TCreditAccount[];
  stats: TBrandStats;
  defaultBillingEntityId: Maybe<
    NonNullable<TBrand["defaultBillingEntity"]>["id"]
  >;
  pipedriveCustomFieldValues: TPipedriveCustomFieldValue[];

  constructor(src: Omit<BrandModel, "__nominal">) {
    this.id = src.id;
    this.name = src.name;
    this.code = src.code;
    this.image = src.image;
    this.client = src.client;
    this.archived_at = src.archived_at;
    this.contacts = src.contacts;
    this.stats = src.stats;
    this.creditAccounts = src.creditAccounts || [];
    this.defaultBillingEntityId = src.defaultBillingEntityId;
    this.pipedriveCustomFieldValues = src.pipedriveCustomFieldValues;
  }
}

export class ClientModel {
  @observable id: string;

  @observable name: string;
  @observable billingEntities?: {
    id: string;
    internal_name: string;
  }[] = [];

  constructor(src: Omit<ClientModel, "__nominal" | "billingEntitiesOptions">) {
    makeObservable(this);
    this.id = src.id;
    this.name = src.name;
    this.billingEntities = src.billingEntities;
  }

  @computed get billingEntitiesOptions() {
    return this.billingEntities?.map((i) => ({
      label: i.internal_name,
      value: i.id,
    }));
  }
}
